import { useMemo, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import type { GridColDef } from '@mui/x-data-grid';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  civilToday,
  formatCivilDate,
  isApiError,
  links,
  resolveFormattingLocale,
  useListSearch,
} from '@eis/core';
import {
  InfiniteDataGrid,
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  cardSurfaceSx,
} from '@eis/patterns';
import { claimsPermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/claims';
import { amountText, toClaimRows, type ClaimRow } from '../model/claims';
import { ClaimStatusChip } from './ClaimStatusChip';

/** The catalog's sortable keys, by column (CLAIM_FILTER_CATALOG). */
const SORT_KEYS: Readonly<Record<string, string>> = {
  claimType: 'claimType',
  accountCode: 'accountCode',
  damageDate: 'damageDate',
  status: 'status',
};

/** Newest damage first (U-1), unless the reader orders otherwise. */
const DEFAULT_ORDER = '-damageDate';

/** A 5xx or no answer: claimservice is down — an outage, never "no claims". */
export function isClaimServiceDown(error: unknown): boolean {
  return !isApiError(error) || error.status >= 500;
}

/**
 * Schäden — the tenant's claims (SDD-018 §9, ui-spec §7; modelled on v1's claims list, read-only).
 *
 * Filtering, search, order and paging are claimservice's: the property filter over the claims
 * catalog (status, Schadensart, Schadendatum, policy, group = `accountCode`), its search, its
 * order, one infinite scroll. Everything is in the URL, so a filtered list is a link. No inline
 * "Sachbearbeiter zuweisen" — assigning stays in the suite (C-12).
 */
export function ClaimListPage(): ReactNode {
  const { t, i18n } = useTranslation('claims');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const url = useListSearch();
  const search = url.params.search ?? '';
  const order = url.params.order ?? DEFAULT_ORDER;
  const catalog = useFilterCatalog('claims');
  const list = useInfiniteList('claims', { ...url.params, order });
  const today = civilToday();
  const rows = useMemo(() => toClaimRows(list.items, today), [list.items, today]);
  const filtered = search !== '' || (url.params.filters ?? '') !== '';

  const columns = useMemo<GridColDef<ClaimRow>[]>(
    () => [
      {
        field: 'claimNumber',
        headerName: t('list.columns.number'),
        width: 160,
        renderCell: ({ row }) => (
          <Link
            href={links.claims.claim(row.code)}
            sx={{ fontFamily: 'monospace' }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void navigate({ href: links.claims.claim(row.code) });
            }}
          >
            {row.claimNumber}
          </Link>
        ),
      },
      {
        field: 'policyCode',
        headerName: t('list.columns.policy'),
        width: 150,
        renderCell: ({ row }) => (
          <Link
            href={links.insurance.policy(row.policyCode)}
            sx={{ fontFamily: 'monospace' }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void navigate({ href: links.insurance.policy(row.policyCode) });
            }}
          >
            {row.policyCode}
          </Link>
        ),
      },
      { field: 'claimType', headerName: t('list.columns.type'), width: 130 },
      {
        field: 'accountCode',
        headerName: t('list.columns.account'),
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', height: '100%' }}>
            <span style={{ fontFamily: 'monospace' }}>
              {row.accountCode === '' ? '—' : row.accountCode}
            </span>
            {row.groupAccount && (
              <Chip size="small" variant="outlined" label={t('list.groupAccount')} />
            )}
          </Stack>
        ),
      },
      {
        field: 'damageDate',
        headerName: t('list.columns.damageDate'),
        width: 120,
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'daysOpen',
        headerName: t('list.columns.daysOpen'),
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => <Chip size="small" label={row.daysOpen} />,
      },
      {
        field: 'status',
        headerName: t('list.columns.status'),
        width: 150,
        renderCell: ({ row }) => <ClaimStatusChip status={row.status} />,
      },
      {
        field: 'adjuster',
        headerName: t('list.columns.adjuster'),
        width: 150,
        sortable: false,
        valueFormatter: (value: string) => (value === '' ? t('list.unassigned') : value),
      },
      {
        field: 'initialCost',
        headerName: t('list.columns.initialCost'),
        width: 140,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | undefined) =>
          amountText(value, locale, t('list.notSubmitted')),
      },
      {
        field: 'reimbursementAmount',
        headerName: t('list.columns.reimbursement'),
        width: 140,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | undefined) =>
          amountText(value, locale, t('list.noReimbursement')),
      },
    ],
    [t, locale, navigate],
  );

  const header = (
    <PageHeader
      title={t('list.title')}
      description={t('list.subtitle')}
      actions={
        <RequirePermission anyOf={[p.claims.create]}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              void navigate({ href: links.claims.newClaim() });
            }}
          >
            {t('list.create')}
          </Button>
        </RequirePermission>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
            placeholder={t('list.search')}
          />
          <InfiniteDataGrid
            rows={rows}
            list={list}
            columns={columns}
            ariaLabel={t('list.ariaLabel')}
            order={order}
            onOrderChange={(next) => {
              url.set({ order: next });
            }}
            sortKeys={SORT_KEYS}
            errorDescription={
              isClaimServiceDown(list.error) ? t('list.error.unavailable') : t('list.error.list')
            }
            emptyTitle={filtered ? t('list.empty.filteredTitle') : t('list.empty.title')}
            emptyDescription={filtered ? t('list.empty.filtered') : t('list.empty.description')}
            onRowClick={(row) => {
              void navigate({ href: links.claims.claim(row.code) });
            }}
          />
        </Stack>
      </Paper>
    </PageLayout>
  );
}
